import React, { useEffect, useRef } from 'react'
import { Button, Flex, Typography } from 'components'
import styled, { css } from 'styled-components'
import { UilMessage, UilMicrophone, UilStopCircle } from '@iconscout/react-unicons'
import {
  borderRadius,
  color,
  fontSize,
  fontWeight,
  padding,
  shadow,
} from '@evrekadev/evreka-ui-components'
import { UseAssistantHelpers } from 'ai/react'
import 'regenerator-runtime/runtime'
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition'

export type InputAreaProps = Pick<
  UseAssistantHelpers,
  'input' | 'handleInputChange' | 'submitMessage' | 'threadId'
>

export const InputArea: React.FC<InputAreaProps> = ({
  handleInputChange,
  input,
  submitMessage,
  threadId,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const {
    browserSupportsSpeechRecognition,
    finalTranscript,
    interimTranscript,
    isMicrophoneAvailable: _isMicrophoneAvailable,
    listening,
  } = useSpeechRecognition()

  useEffect(() => {
    if (finalTranscript) {
      handleInputChange({
        target: { value: finalTranscript },
      } as React.ChangeEventHandler<HTMLInputElement>['arguments'])
    }
  }, [finalTranscript])

  return (
    <InputAreaStyled onSubmit={submitMessage}>
      <ChatInput>
        {browserSupportsSpeechRecognition && (
          <MicroPhoneButton
            listening={listening}
            iconLeft={listening ? UilStopCircle : UilMicrophone}
            variant="text"
            color="secondary"
            onlyIcon
            size="medium"
            type="button"
            onClick={() =>
              listening ? SpeechRecognition.stopListening() : SpeechRecognition.startListening()
            }
          />
        )}
        <textarea
          value={listening ? interimTranscript : input}
          onChange={handleInputChange}
          placeholder={listening ? 'Listening...' : 'Type a message...'}
          rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              buttonRef.current?.click()
            }
          }}
        />
        <Button
          ref={buttonRef}
          className="primary"
          variant="outline"
          iconLeft={UilMessage}
          onlyIcon
          size="medium"
          type="submit"
          disabled={!listening && !input}
        />
      </ChatInput>
      <Flex gap={0.5} between="zero">
        <Typography fontSize="text_xxs" color="grey400">
          {threadId ? `Thread ID: ${threadId}` : 'Evreka AI may be incorrect.'}
        </Typography>
      </Flex>
    </InputAreaStyled>
  )
}

const ChatInput = styled.div<{ lines?: number }>`
  ${shadow('xs')}
  ${borderRadius(8)}
  ${padding([8, 4])}
  ${padding({ r: 8 })}
  display: flex;
  gap: 0.5rem;
  border: 1px solid ${color('grey200')};
  align-items: end;

  min-height: 3rem;

  textarea,
  textarea:hover,
  textarea:focus {
    ${padding([8, 0])}
    ${fontSize('text_sm')}
    ${fontWeight('regular')}
    height: 100%;
    flex-grow: 1;
    border-radius: 0;
    border: none;
    box-shadow: none;
    color: ${color('grey900')};
  }

  textarea::placeholder {
    ${fontSize('text_sm')}
    ${fontWeight('regular')}
    color: ${color('grey400')};
  }
`

const MicroPhoneButton = styled(Button)<{ listening: boolean }>`
  ${({ listening }) =>
    listening &&
    css`
      svg {
        fill: ${color('red500')};
      }
      border-color: ${color('red500')};
      &:hover {
        background-color: ${color('red25')};
      }
    `}
`

const InputAreaStyled = styled.form`
  ${padding([16, 8, 8, 12])}
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-top: 1px solid ${color('grey200')};

  button.primary {
    flex-shrink: 0;
    border-color: ${color('pink500')};
    svg {
      fill: ${color('pink500')};
    }
    &:hover {
      background-color: ${color('pink25')};
    }
  }

  textarea {
    resize: none;
  }
`
